import * as React from "react";
import {
  IXGUIFieldBase,
  TOnChange,
  TXGUIField,
} from "../../../utils/interfaces/gui/fields.interfaces";

import { UNITS } from "../../../utils/constants/gui/units";
import { SIDES_NAME } from "../../../utils/constants/gui/sidesName";
import { TSide } from "../../../utils/types/gui/field.types";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

const compareProps = (prevProps: any, nextProps: any) => {
  // return isEqual(prevProps, nextProps);
  return prevProps.currentValue === nextProps.currentValue;
};

interface XGUISideFieldProps extends IXGUIFieldBase {
  onChange: TOnChange<TSide | undefined>;
  currentValue: TSide;
}

const XGUISideField: React.FC<XGUISideFieldProps> = React.memo(
  ({ field, currentValue, onChange }) => {
    const value = currentValue ?? field.defaultValue ?? {};
    const bp = useBreakpoint();

    const setValue = (_value: TSide) => {
      onChange(_value, field.name, bp);
    };

    const id = `XGUI-FIELD-${field.name}`;

    const section = (
      <React.Fragment>
        <Typography
          component="label"
          htmlFor={id}
          textAlign="center"
          display="inline-block"
          width="100%"
        >
          {field.label}
        </Typography>
        <Stack spacing={1} alignItems="center" mt={1}>
          <Field
            field={field}
            id={id}
            side="Top"
            value={value}
            setValue={setValue}
          />
          <Stack spacing={1} justifyContent="space-between" direction="row">
            <Field
              field={field}
              id={id}
              side="Left"
              value={value}
              setValue={setValue}
            />

            <Field
              field={field}
              id={id}
              side="Right"
              value={value}
              setValue={setValue}
            />
          </Stack>
          <Field
            field={field}
            id={id}
            side="Bottom"
            value={value}
            setValue={setValue}
          />
        </Stack>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />
      </React.Fragment>
    );

    return section;
  },
  compareProps
);

export default XGUISideField;

interface FieldProps {
  id: string;
  side: "Top" | "Right" | "Bottom" | "Left";
  value: TSide;
  setValue: (newValue: TSide) => void;
  field: TXGUIField;
}

const Field: React.FC<FieldProps> = ({ id, side, value, field, setValue }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sideValue = value[side];

  const [unit, setUnit] = React.useState<string>(
    sideValue ? (sideValue ?? "").replace(/[0-9]/g, "") : "px"
  );

  React.useEffect(() => {
    if (sideValue) {
      if (!sideValue.includes(unit)) {
        const _unit = sideValue.replace(/[0-9]/gi, "");

        if (_unit.length > 0) setUnit(_unit);
      }
    }
  }, [sideValue]);

  return (
    <Stack
      // style={{ flex: 1, maxWidth: 120 }}
      className="XGUIFieldOption Side"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <TextField
        id={id + "-" + side}
        fullWidth
        hiddenLabel
        size="small"
        type="number"
        placeholder={SIDES_NAME[side]}
        value={sideValue && parseInt(sideValue.replace(/[^0-9]/gi, "") ?? "0")}
        onChange={(e) => {
          const _val = e.target.value + unit;
          setValue({ ...value, [side]: _val });
        }}
        InputProps={{
          endAdornment: (
            <>
              <Button
                id={`XGUIFieldOption-Btn-${name}`}
                aria-controls={
                  open ? `XGUIFieldOption-Menu-${name}` : undefined
                }
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                color="inherit"
              >
                {unit}
              </Button>
              <Menu
                id={`XGUIFieldOption-Menu-${name}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": `XGUIFieldOption-Btn-${name}`,
                }}
                sx={{
                  ".MuiList-root": {
                    p: 0,

                    ".MuiButtonBase-root": {
                      fontSize: 10,
                      px: 1,
                      fontWeight: 600,
                      justifyContent: "center",
                    },
                  },
                }}
              >
                {(field.units ?? UNITS).map((_unit) => {
                  return (
                    <MenuItem
                      key={_unit}
                      onClick={() => {
                        setUnit((oldUnit) => {
                          const _value =
                            (sideValue ?? "0").replace(oldUnit, "") + _unit;

                          if (sideValue || _unit === "auto")
                            setValue({ ...value, [side]: _value });

                          return _unit;
                        });

                        handleClose();
                      }}
                    >
                      {_unit}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ),
        }}
      />
    </Stack>
  );
};
